"use client";

import React, { useState, useEffect } from "react";

interface CountdownTimerProps {
  targetDate: string;
  labels?: {
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
  };
  className?: string;
  variant?: "default" | "compact";
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  targetDate,
  labels = {
    days: "Tage",
    hours: "Std",
    minutes: "Min",
    seconds: "Sek",
  },
  className = "",
  variant = "default",
}) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000),
        });
      } else {
        // Timer expired
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const getDisplayValue = (value: number): string => {
    return value.toString().padStart(2, '0');
  };

  const getUnitLabel = (unit: keyof TimeLeft): string => {
    return labels[unit] || unit;
  };

  if (variant === "compact") {
    return (
      <div className={`flex justify-center gap-2 ${className}`}>
        {Object.entries(timeLeft).map(([unit, value]) => (
          <div key={unit} className="text-center">
            <div className="bg-abis-accent text-abis-primary font-bold text-lg px-2 py-1 rounded min-w-[40px]">
              {getDisplayValue(value)}
            </div>
            <div className="text-xs mt-1 text-gray-600">
              {getUnitLabel(unit as keyof TimeLeft)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`flex justify-center gap-4 ${className}`}>
      {Object.entries(timeLeft).map(([unit, value]) => (
        <div key={unit} className="text-center">
          <div className="bg-abis-accent text-abis-primary font-bold text-2xl md:text-3xl px-4 py-2 rounded-lg min-w-[60px]">
            {getDisplayValue(value)}
          </div>
          <div className="text-white/80 text-sm mt-1">
            {getUnitLabel(unit as keyof TimeLeft)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CountdownTimer;
